import React, { useEffect, useState } from 'react';
import { Modal, RNLLoader } from '../UI';
import * as api from '../../api';

const NAVY = '#0b318b';
const INDIGO = '#4338ca';

const FileIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6" />
    <path d="M12 12v6" /><path d="M9 15h6" />
  </svg>
);

const ClipboardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="2" width="8" height="4" rx="1" />
    <path d="M9 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3" />
    <path d="M9 12h6" /><path d="M9 16h6" />
  </svg>
);

const Chevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

function MethodRow({ Icon, title, desc, accent, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
        border: `1px solid ${hovered ? accent : '#e2e8f0'}`,
        background: hovered ? `${accent}0d` : 'var(--bg-card)',
        borderRadius: 12, padding: '14px 16px',
        transition: 'all 0.15s ease',
        boxShadow: hovered ? '0 4px 12px -4px rgba(0,0,0,0.12)' : 'none',
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: hovered ? accent : `${accent}1a`,
        color: hovered ? '#fff' : accent,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.15s ease',
      }}>
        <Icon />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: accent, marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{desc}</div>
      </div>
      <Chevron />
    </div>
  );
}

export default function VoucherMethodModal({ onClose, onSelectMethod, onLoadRequisition, toast }) {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.listPaymentVoucherMethods(true);
        const data = Array.isArray(res) ? res : (res?.methods || []);
        if (!cancelled) setMethods(data);
      } catch (e) {
        if (!cancelled) toast.show(e.message, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      title="Select Voucher Creation Method"
      onClose={onClose}
      width={620}
      headerBg={INDIGO}
    >
      <div style={{ fontSize: 12.5, color: '#64748b', marginTop: -6, marginBottom: 16 }}>
        Choose the type of payment voucher to create
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center' }}><RNLLoader title="Loading methods…" /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {methods.map(m => (
            <MethodRow
              key={m.id}
              Icon={FileIcon}
              accent={INDIGO}
              title={m.name}
              desc={m.description || 'Create a payment voucher of this type.'}
              onClick={() => onSelectMethod(m)}
            />
          ))}

          {methods.length === 0 && (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No voucher creation methods are configured yet. An administrator can add them under
              Administration → Payment Voucher Methods.
            </div>
          )}

          {/* Routing option — not a creation method, so it sits below a divider */}
          <div style={{ borderTop: '1px dashed #cbd5e1', marginTop: 6, paddingTop: 12 }}>
            <MethodRow
              Icon={ClipboardIcon}
              accent={NAVY}
              title="Load Approved Requisition"
              desc="Route to LOAD APP REQ — create a voucher from an existing approved requisition."
              onClick={onLoadRequisition}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
